import React, {
  useReducer,
  createContext,
  useEffect,
  useCallback,
  useContext,
  useRef
} from 'react';
import FormBuilderStatusesView from '../view/FormBuilderStatusesView';
import generateId from '../../utils/generateId';
import fetchAPI from '../../utils/fetchAPI';
import { FetchCallbacksContext } from '../../internal-components/FetchCallbacks';
import useGlobalVariables from '../../hooks/useGlobalVariables';

export const replaceElements = (list, previousIndex, newIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(previousIndex, 1);
  result.splice(newIndex, 0, removed);

  return result;
};

const StatusesReducer = (state, action) => {
  const { type, payload, index } = action;

  switch (type) {
    case 'load':
      return [...payload];
    case 'add':
      return [...state, payload];
    case 'update': {
      return state.map(s => (s.position === index ? { ...s, ...payload } : s));
    }
    case 'delete': {
      return state
        .map(s =>
          s.position < index
            ? s
            : s.position === index
            ? null
            : { ...s, position: s.position - 1 }
        )
        .filter(s => s);
    }
    default:
      return state;
  }
};
export const Context = createContext(null);

// const mock = [
//   {
//     statusName: 'status A',
//     isLocked4EndUsers: true,
//     position: 0,
//     id: generateId()
//     options: []
//   },
// ];

const translate2Local = arr =>
  arr.map(s => ({
    statusName: s.label,
    position: s.position,
    isLocked4EndUsers: Boolean(s.lockRules && s.lockRules.role === 'end_user'),
    options: s.options,
    id: s.internalId
  }));

const translate2Server = arr =>
  arr
    .map(s => ({
      label: s.statusName,
      position: s.position,
      options: s.options,
      lockRules: s.isLocked4EndUsers ? { role: 'end_user' } : undefined,
      internalId: s.id
    }))
    .filter(s => s.label.trim() !== '');
/**
 *
 * @param {Objext} form - form object
 * @param {String} formId - we can be certain that it's poulated because this screen is unavilable for new (with no id) forms
 * @param {Fn} updateStatusesOnRedux - function to update local state of the statuses (update form object), note this doesn't update on server side!
 */
const FormBuilderStatusesEngine = ({
  form,
  formId,
  updateStatusesOnRedux = sttsArr => sttsArr
}) => {
  const [data, dispatch] = useReducer(StatusesReducer, []);
  const { openSnackbar } = useContext(FetchCallbacksContext);
  const skipUpdate = useRef(true); // prevent update from triggering API call
  const isFirstRender = useRef(true);
  const { setIsLoading } = useGlobalVariables();

  useEffect(() => {
    const localFormat = translate2Local(form.customStatuses);
    dispatch({ type: 'load', payload: localFormat });
  }, []);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (skipUpdate.current) {
      skipUpdate.current = false;
      return;
    }

    const serverFormat = translate2Server(data);
    fetchAPI(`/forms/v1/forms/${formId}`, {
      method: 'PUT',
      body: { ...form, customStatuses: serverFormat }
    })
      .then(() => {
        updateStatusesOnRedux(serverFormat);
      })
      .catch(e => openSnackbar('error', 'snackbars.statuses.save.err'));
  }, [data]);

  const onDragEnd = useCallback(
    result => {
      if (!result.destination) {
        return;
      }
      if (result.destination.index === result.source.index) {
        return;
      }
      const previousIndex = result.source.index;
      const newIndex = result.destination.index;
      const sortedList = replaceElements(
        data,
        previousIndex,
        newIndex
      ).map((s, i) => ({ ...s, position: i }));
      dispatch({
        type: 'load',
        payload: sortedList
      });
    },
    [data]
  );

  const addNewStatus = useCallback(() => {
    const newStatusNode = {
      statusName: '',
      isLocked4EndUsers: false,
      position: data.length,
      id: generateId(),
      options: []
    };
    skipUpdate.current = true;
    dispatch({ type: 'add', payload: newStatusNode });
  }, [data]);

  const getStatusUsage = useCallback(
    async id => {
      setIsLoading(true);
      const usage = [];
      await fetchAPI(`/forms/v1/forms/${formId}/statuses?status=${id}`)
        .then(response => {
          const results = response.results || [];

          results.forEach(loc => {
            usage.push(loc.dependantEntityType);
          });
        })
        .finally(() => {
          setIsLoading(false);
        });

      // need to add more places to check in the future

      return usage;
    },
    [form, formId]
  );

  const viewProps = {
    data,
    dispatch,
    onDragEnd,
    addNewStatus
  };
  const contextProps = { data, dispatch, getStatusUsage };

  return (
    <Context.Provider value={contextProps}>
      <FormBuilderStatusesView {...viewProps} />
    </Context.Provider>
  );
};

export default FormBuilderStatusesEngine;
